import { Dialog, Transition } from '@headlessui/react';
import { Minus, Plus, X } from 'lucide-react';
import Cropper, { Area } from 'react-easy-crop';

import { Fragment, useCallback, useState } from 'react';

import { useNamespace, useTranslation } from '@splitfiesta/i18n';
import { Button } from '@splitfiesta/ui';

import { CropArea, cropImage } from '@/utils/imageOptimization';
import { toast } from '@/utils/toast';

interface ImageCropperModalProps {
  isOpen: boolean;
  imageSrc: string;
  cropShape: 'round' | 'rect';
  aspect: number;
  onConfirm: (croppedFile: File) => void;
  onCancel: () => void;
}

export function ImageCropperModal({
  isOpen,
  imageSrc,
  cropShape,
  aspect,
  onConfirm,
  onCancel,
}: ImageCropperModalProps) {
  const { t } = useTranslation();
  useNamespace('common');

  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<CropArea | null>(
    null
  );
  const [isProcessing, setIsProcessing] = useState(false);

  const onCropComplete = useCallback(
    (_croppedArea: Area, croppedAreaPixels: Area) => {
      setCroppedAreaPixels(croppedAreaPixels);
    },
    []
  );

  const handleConfirm = async () => {
    if (!croppedAreaPixels) return;

    try {
      setIsProcessing(true);
      const croppedFile = await cropImage(
        imageSrc,
        croppedAreaPixels,
        'cropped-image.jpg'
      );
      onConfirm(croppedFile);
    } catch {
      toast.error(t('common:upload.cropError'));
    } finally {
      setIsProcessing(false);
    }
  };

  const handleZoomIn = () => {
    setZoom(prev => Math.min(prev + 0.1, 3));
  };

  const handleZoomOut = () => {
    setZoom(prev => Math.max(prev - 0.1, 1));
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onCancel}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/50 dark:bg-black/70" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-3 sm:p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-lg transform overflow-hidden rounded-2xl bg-white dark:bg-gray-800 shadow-xl transition-all">
                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
                  <Dialog.Title
                    as="h3"
                    className="text-lg font-medium text-gray-900 dark:text-gray-100"
                  >
                    {t('common:upload.cropImage')}
                  </Dialog.Title>
                  <button
                    onClick={onCancel}
                    className="p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                    aria-label={t('common:actions.close')}
                  >
                    <X className="h-5 w-5 text-gray-500 dark:text-gray-400" />
                  </button>
                </div>

                {/* Cropper Area */}
                <div className="relative h-[300px] sm:h-[350px] bg-gray-900">
                  <Cropper
                    image={imageSrc}
                    crop={crop}
                    zoom={zoom}
                    aspect={aspect}
                    cropShape={cropShape}
                    showGrid={cropShape === 'rect'}
                    onCropChange={setCrop}
                    onZoomChange={setZoom}
                    onCropComplete={onCropComplete}
                  />
                </div>

                {/* Zoom Controls */}
                <div className="flex items-center justify-center gap-4 p-4 border-t border-gray-200 dark:border-gray-700">
                  <button
                    onClick={handleZoomOut}
                    disabled={zoom <= 1}
                    className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    aria-label={t('common:upload.zoomOut')}
                  >
                    <Minus className="h-5 w-5 text-gray-600 dark:text-gray-300" />
                  </button>

                  <input
                    type="range"
                    min={1}
                    max={3}
                    step={0.01}
                    value={zoom}
                    onChange={e => setZoom(Number(e.target.value))}
                    className="w-32 sm:w-48 h-2 bg-gray-200 dark:bg-gray-600 rounded-lg appearance-none cursor-pointer accent-blue-600"
                    aria-label={t('common:upload.zoom')}
                  />

                  <button
                    onClick={handleZoomIn}
                    disabled={zoom >= 3}
                    className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    aria-label={t('common:upload.zoomIn')}
                  >
                    <Plus className="h-5 w-5 text-gray-600 dark:text-gray-300" />
                  </button>
                </div>

                {/* Action Buttons */}
                <div className="flex flex-col-reverse sm:flex-row gap-3 p-4 border-t border-gray-200 dark:border-gray-700 sm:justify-end">
                  <Button
                    variant="outline"
                    onClick={onCancel}
                    disabled={isProcessing}
                    className="w-full sm:w-auto min-h-[44px]"
                  >
                    {t('common:actions.cancel')}
                  </Button>
                  <Button
                    variant="primary"
                    onClick={handleConfirm}
                    disabled={isProcessing || !croppedAreaPixels}
                    className="w-full sm:w-auto min-h-[44px]"
                  >
                    {isProcessing
                      ? t('common:upload.processing')
                      : t('common:actions.confirm')}
                  </Button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
